import { Badge, Button, Icon, Sheet } from '@astral/ui';
import { isValidEmojiList, type StickerAsset, type StickerType } from '@astral/shared';
import { useT, type TranslationKey } from '../providers/I18nProvider.js';

export interface PackReviewSheetProps {
  open: boolean;
  mode: 'publish' | 'sync';
  title: string;
  stickerType: StickerType;
  assets: StickerAsset[];
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/** Compact, mobile-first final review before publishing or syncing a pack. */
export function PackReviewSheet({
  open,
  mode,
  title,
  stickerType,
  assets,
  busy,
  onConfirm,
  onClose,
}: PackReviewSheetProps): JSX.Element {
  const t = useT();
  const invalid = assets.filter((a) => !isValidEmojiList(a.emojiList)).length;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('publish.reviewTitle')}
      footer={
        <Button
          fullWidth
          size="lg"
          loading={busy}
          disabled={assets.length === 0 || invalid > 0}
          leftIcon={<Icon name="sparkles" size={18} />}
          onClick={onConfirm}
        >
          {mode === 'publish' ? t('publish.action') : t('publish.update')}
        </Button>
      }
    >
      <div className="review">
        <div className="review__head">
          <span className="review__title">{title}</span>
          <div className="review__meta">
            <Badge tone="accent">{t(`stickerTypes.${stickerType}` as TranslationKey)}</Badge>
            <Badge>{t('stickers.count', { count: assets.length })}</Badge>
          </div>
        </div>

        <div className="review__list">
          {assets.map((asset, index) => (
            <div key={asset.id} className="review__row">
              <span className="review__index">{index + 1}</span>
              <span className="review__thumb">
                {asset.kind === 'video' ? (
                  <video src={asset.src} muted playsInline />
                ) : (
                  <img src={asset.src} alt="" />
                )}
              </span>
              <span className="review__emoji">{asset.emojiList.join(' ')}</span>
            </div>
          ))}
        </div>
      </div>
    </Sheet>
  );
}
